import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Youtube, Download, Sparkles, ListVideo, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export const YouTubeImporter = ({ onImported }) => {
  const [channel, setChannel] = useState("@SmartNewsIT");
  const [playlists, setPlaylists] = useState([]);
  const [loadingPl, setLoadingPl] = useState(false);
  const [source, setSource] = useState({ type: "channel", id: "", name: "" });
  const [limit, setLimit] = useState(30);
  const [status, setStatus] = useState("draft");
  const [useAi, setUseAi] = useState(true);
  const [themeFromPlaylist, setThemeFromPlaylist] = useState(true);
  const [starting, setStarting] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const loadPlaylists = async () => {
    setLoadingPl(true);
    try {
      const { data } = await api.get("/admin/youtube/playlists", { params: { channel } });
      setPlaylists(data.playlists || []);
      if (!data.playlists?.length) toast.info("Nessuna playlist trovata per questo canale.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Impossibile leggere le playlist.");
    } finally {
      setLoadingPl(false);
    }
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  const pollJob = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/youtube/import/${jobId}`);
        setJob(data);
        if (data.status === "done" || data.status === "error") {
          clearInterval(pollRef.current);
          setStarting(false);
          if (data.status === "done") {
            toast.success(`Importazione completata: ${data.created} create, ${data.skipped} già presenti.`);
            onImported?.();
          } else {
            toast.error(`Importazione non riuscita: ${data.error || "errore"}`);
          }
        }
      } catch {
        clearInterval(pollRef.current);
        setStarting(false);
      }
    }, 2000);
  };

  const startImport = async () => {
    setStarting(true);
    setJob({ status: "running", total: 0, processed: 0, created: 0, skipped: 0 });
    try {
      const payload = {
        source_type: source.type,
        source_id: source.type === "playlist" ? source.id : "",
        channel,
        limit: Number(limit) || 30,
        status,
        use_ai: useAi,
        playlist_name: source.type === "playlist" && themeFromPlaylist ? source.name : "",
      };
      const { data } = await api.post("/admin/youtube/import", payload);
      pollJob(data.job_id);
    } catch (err) {
      setStarting(false);
      setJob(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Avvio importazione non riuscito.");
    }
  };

  const pct = job && job.total ? Math.round((job.processed / job.total) * 100) : 0;
  const running = job?.status === "running";

  return (
    <div className="border border-border rounded-sm p-5 space-y-5" data-testid="youtube-importer">
      <div className="flex items-center gap-2">
        <Youtube className="h-5 w-5 text-accent" />
        <h3 className="font-serif text-lg font-bold">Importa da YouTube</h3>
      </div>
      <p className="text-sm text-muted-foreground -mt-2">
        Crea automaticamente le schede a partire dai tuoi video: titolo, data di pubblicazione e miniatura
        vengono compilati; con l'IA anche leader, Paese, tema e un breve riassunto. Aggiungi trascrizione e
        traduzione in un secondo momento.
      </p>

      {/* Canale + carica playlist */}
      <div className="flex flex-col sm:flex-row sm:items-end gap-2">
        <div className="flex-1">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Canale YouTube</label>
          <Input value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="@SmartNewsIT" className="rounded-sm mt-1" data-testid="yt-channel-input" />
        </div>
        <Button onClick={loadPlaylists} disabled={loadingPl} variant="outline" className="rounded-sm gap-1.5" data-testid="yt-load-playlists-btn">
          {loadingPl ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListVideo className="h-4 w-4" />}
          Carica playlist
        </Button>
      </div>

      {/* Scelta sorgente */}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cosa importare</label>
        <div className="mt-2 space-y-2">
          <label className="flex items-center gap-3 p-3 border border-border rounded-sm cursor-pointer" data-testid="yt-source-channel">
            <input type="radio" name="yt-source" checked={source.type === "channel"} onChange={() => setSource({ type: "channel", id: "", name: "" })} className="h-4 w-4 accent-primary" />
            <span className="text-sm font-medium">Ultimi video caricati sul canale <span className="text-muted-foreground">(dal più recente)</span></span>
          </label>
          {playlists.map((p) => (
            <label key={p.id} className="flex items-center gap-3 p-3 border border-border rounded-sm cursor-pointer" data-testid={`yt-source-pl-${p.id}`}>
              <input type="radio" name="yt-source" checked={source.type === "playlist" && source.id === p.id} onChange={() => setSource({ type: "playlist", id: p.id, name: p.title })} className="h-4 w-4 accent-primary" />
              <span className="text-sm font-medium truncate">Playlist: {p.title}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Opzioni */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Quanti video (max 50)</label>
          <Input type="number" min={1} max={50} value={limit} onChange={(e) => setLimit(e.target.value)} className="rounded-sm mt-1" data-testid="yt-limit-input" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Stato dei video importati</label>
          <div className="flex gap-2 mt-1">
            {[["draft", "Bozze"], ["published", "Pubblicati"]].map(([k, l]) => (
              <Button key={k} type="button" size="sm" variant={status === k ? "default" : "outline"} className="rounded-sm flex-1" onClick={() => setStatus(k)} data-testid={`yt-status-${k}`}>{l}</Button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-3 text-sm cursor-pointer" data-testid="yt-use-ai">
          <input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} className="h-4 w-4 accent-primary" />
          <span className="inline-flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-primary" /> Precompila con IA (leader, Paese, tema, riassunto)</span>
        </label>
        {source.type === "playlist" && (
          <label className="flex items-center gap-3 text-sm cursor-pointer" data-testid="yt-theme-from-playlist">
            <input type="checkbox" checked={themeFromPlaylist} onChange={(e) => setThemeFromPlaylist(e.target.checked)} className="h-4 w-4 accent-primary" />
            <span>Usa il nome della playlist come tema di partenza</span>
          </label>
        )}
      </div>

      <Button onClick={startImport} disabled={starting || running} className="rounded-sm gap-1.5" data-testid="yt-start-import-btn">
        {starting || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        Avvia importazione
      </Button>

      {/* Progresso */}
      {job && (
        <div className="border border-border rounded-sm p-4 space-y-2" data-testid="yt-import-progress">
          <div className="flex items-center justify-between text-sm">
            <span className="inline-flex items-center gap-2">
              {running ? <RefreshCw className="h-4 w-4 animate-spin text-primary" /> : null}
              {job.status === "done" ? "Completata" : job.status === "error" ? "Errore" : "Importazione in corso…"}
            </span>
            <span className="text-muted-foreground">{job.processed || 0}/{job.total || 0}</span>
          </div>
          <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="default" className="rounded-sm text-[10px]">{job.created || 0} create</Badge>
            <Badge variant="secondary" className="rounded-sm text-[10px]">{job.skipped || 0} già presenti</Badge>
          </div>
          {running && <p className="text-xs text-muted-foreground">Puoi lasciare aperta questa pagina: l'importazione continua anche se cambi scheda.</p>}
        </div>
      )}
    </div>
  );
};
